import React, { useState } from "react";
import { toast } from "react-toastify";
import CryptoJS from "crypto-js";
import apiClient from "../utils/apiConfig";
import errorHandler from "../utils/errorHandler";
import { deriveAuthKeyAsync, deriveKEKAsync, generateDEK, wrapDEKAsync } from "../utils/zkEncryption";
import ModalShell from "./ModalShell";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ForgotPassword = ({ onClose, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: Enter Email, 2: Enter OTP & New Password
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const requestOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiClient.post(`${API}/auth/request-password-otp`, { email });
      toast.success("OTP sent to your email!");
      setStep(2);
    } catch (error) {
      errorHandler.handle(error, 'forgot-password-otp', 'Failed to send verification code. Please check your email address.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match!");
      return;
    }

    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return;
    }

    setLoading(true);
    try {
      // ZK key derivation — password never sent to server
      const authKey = await deriveAuthKeyAsync(newPassword, email.toLowerCase());
      const newAuthHash = CryptoJS.SHA256(authKey).toString(CryptoJS.enc.Hex);
      const newKEK = await deriveKEKAsync(newPassword, email.toLowerCase());
      const newDEK = generateDEK(); // new random DEK (vault data will be inaccessible)
      const newWrappedDEK = await wrapDEKAsync(newDEK, newKEK);

      await apiClient.post(`${API}/auth/change-password`, {
        email,
        otp,
        newAuthHash,
        newWrappedDEK
      });
      toast.success("Password reset successfully! Note: previous vault data is inaccessible with new password. Please sign in.");
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      errorHandler.handle(error, 'forgot-password-reset', 'Failed to reset password. Please check your OTP and try again.');
    } finally{
      setLoading(false);
    }
  };

  return (
    <ModalShell isOpen onClose={onClose} maxWidth="max-w-md" className="p-6 sm:p-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">Forgot Password</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-2xl transition"
          >
            ×
          </button>
        </div>

        {step === 1 ? (
          // Step 1: Enter Email
          <form onSubmit={requestOTP}>
            <div className="mb-6">
              <div className="bg-white/5 border border-white/10 p-4 mb-4 rounded-lg">
                <p className="text-[#5B8DEF] text-sm">
                  🔐 Enter your email address and we'll send you an OTP to reset your password.
                </p>
              </div>

              <label className="block text-gray-300 text-sm font-semibold mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#3E7BFA]/50 transition-colors"
                placeholder="your@email.com"
                required
              />
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-3 rounded-lg bg-white/5 border border-white/10 text-white font-semibold hover:bg-white/10 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-3 rounded-lg bg-[#0A2472] hover:bg-[#0d2d8a] text-white font-semibold disabled:opacity-50 transition"
              >
                {loading ? "Sending..." : "Send OTP"}
              </button>
            </div>
          </form>
        ) : (
          // Step 2: Enter OTP and New Password
          <form onSubmit={handleResetPassword}>
            <div className="mb-4">
              <div className="bg-green-500/10 border border-green-500/30 p-3 mb-4 rounded-lg">
                <p className="text-green-300 text-sm">
                  ✅ OTP sent to: <strong>{email}</strong>
                </p>
              </div>

              <label className="block text-gray-300 text-sm font-semibold mb-2">
                Enter OTP from Email
              </label>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-center text-2xl placeholder-gray-500 focus:outline-none focus:border-[#3E7BFA]/50 transition-colors"
                placeholder="000000"
                maxLength="6"
                required
              />
              <p className="text-gray-400 text-xs mt-2">
                Check your email for the 6-digit OTP (valid for 10 minutes)
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-gray-300 text-sm font-semibold mb-2">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#3E7BFA]/50 transition-colors pr-12"
                  placeholder="Enter new password"
                  minLength="8"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition"
                  title={showNewPassword ? "Hide password" : "Show password"}
                >
                  {showNewPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              <p className="text-gray-400 text-xs mt-1">
                Minimum 8 characters
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-gray-300 text-sm font-semibold mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#3E7BFA]/50 transition-colors pr-12"
                  placeholder="Confirm new password"
                  minLength="8"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition"
                  title={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => {
                  setStep(1);
                  setOtp("");
                  setNewPassword("");
                  setConfirmPassword("");
                }}
                className="flex-1 py-3 rounded-lg bg-white/5 border border-white/10 text-white font-semibold hover:bg-white/10 transition"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-3 rounded-lg bg-[#0A2472] hover:bg-[#0d2d8a] text-white font-semibold disabled:opacity-50 transition"
              >
                {loading ? "Resetting..." : "Reset Password"}
              </button>
            </div>
          </form>
        )}
    </ModalShell>
  );
};

export default ForgotPassword;
